import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Parse URL parameters for ServiceNow context
 * ServiceNow passes context via URL params from UI Actions / Client Scripts
 * User identity is injected by the SN Client Script via g_user.userID / g_user.userName
 */
export function getServiceNowContext(): {
  sysId: string | null;
  table: string | null;
  token: string | null;
  userId: string | null;
  userName: string | null;
} {
  const params = new URLSearchParams(window.location.search)

  // Get ServiceNow record identifiers
  const sysId = params.get('sys_id') || params.get('sysId') || params.get('record_id')
  const table = params.get('table') || params.get('tableName') || 'incident'
  const token = params.get('token') || null

  // Get user identity (injected by SN Client Script)
  const userId = params.get('user_id') || params.get('sysparm_user_id') || null
  const userName = params.get('user_name') || params.get('sysparm_user_name') || null

  return { sysId, table, token, userId, userName }
}

/**
 * Listen for ServiceNow postMessage context
 * For cases where ServiceNow sends context via postMessage
 */
export function listenForServiceNowContext(
  callback: (sysId: string, table: string, userId?: string, userName?: string) => void
): () => void {
  const handler = (event: MessageEvent) => {
    // Accept messages from ServiceNow domains
    if (!event.origin.includes('service-now.com') && !event.origin.includes('servicenow.com')) {
      return
    }

    console.log('[SNPod] Received postMessage:', event.data)

    const data = event.data
    if (data && typeof data === 'object') {
      const sysId = data.sys_id || data.sysId || data.record_id
      const table = data.table || data.tableName || 'incident'
      const userId = data.user_id || data.userId || undefined
      const userName = data.user_name || data.userName || undefined

      if (sysId) {
        callback(String(sysId), String(table), userId ? String(userId) : undefined, userName ? String(userName) : undefined)
      }
    }
  }

  window.addEventListener('message', handler)
  return () => window.removeEventListener('message', handler)
}

/**
 * Format seconds to MM:SS
 */
export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/**
 * Get sentiment color based on score (1-10)
 */
export function getSentimentColor(score: number): string {
  if (score <= 3) return 'text-green-600'
  if (score <= 6) return 'text-yellow-600'
  return 'text-red-600'
}

/**
 * Get sentiment background color based on score (1-10)
 */
export function getSentimentBgColor(score: number): string {
  if (score <= 3) return 'bg-green-100'
  if (score <= 6) return 'bg-yellow-100'
  return 'bg-red-100'
}

/**
 * Map ServiceNow state numbers to display values
 */
export function getIncidentStateDisplay(state: string | number): string {
  const stateMap: Record<string, string> = {
    '1': 'New',
    '2': 'In Progress',
    '3': 'On Hold',
    '6': 'Resolved',
    '7': 'Closed',
    '8': 'Canceled',
  }
  return stateMap[String(state)] || String(state)
}

/**
 * Map ServiceNow priority numbers to display values
 */
export function getPriorityDisplay(priority: string | number): string {
  const priorityMap: Record<string, string> = {
    '1': 'Critical',
    '2': 'High',
    '3': 'Moderate',
    '4': 'Low',
    '5': 'Planning',
  }
  return priorityMap[String(priority)] || String(priority)
}

/**
 * Get priority color based on ServiceNow priority (1-5)
 */
export function getPriorityColor(priority: string | number): string {
  const p = Number(priority)
  if (p === 1) return 'text-red-600 bg-red-100'
  if (p === 2) return 'text-orange-600 bg-orange-100'
  if (p === 3) return 'text-yellow-600 bg-yellow-100'
  if (p === 4) return 'text-blue-600 bg-blue-100'
  return 'text-gray-600 bg-gray-100'
}
